import { cacheDom } from "../utils/QuerySelectors.js"

const dom = {}
const qs = {
  section: 'data-main-product',
  json: 'data-main-product-json',
  gallery: 'data-pdp-gallery',
  variantId: 'name="id"',
  qtyPlus: 'data-main-qty-plus',
  qtyMinus: 'data-main-qty-minus',
  quantity: 'data-main-product-qty',
  markingGroup: 'data-marking-group',
  letters: 'data-marking-letters',
  addToCart: 'data-main-addtocart',
  form: 'data-type="add-to-cart-form"',
  optionList: 'data-pdp-option-list',
  thickness: 'data-pdp-thickness',
  thicknessValue: 'data-pdp-thickness-value',
  thicknessJson: 'data-variant-thickness',
}
const qsAll = {
  thumbs: 'data-pdp-thumb',
  galleryItems: 'data-gallery-item',
  options: 'data-pdp-option',
  markingOptions: 'name="properties[Marking option]"',
  wishlists: 'data-swaction="addToWishlist"',
  optionSelects: 'data-pdp-select',
}

const state = {
  swiperGallery: null,
  jsonData: null,
  maxLetters: null,
}

const currentVariant = () => state.jsonData.variants.find((variant) => String(variant.id) === String(dom.variantId.value))

// The gallery already contains every product image, so slide to the variant's image instead of
// re-rendering the gallery (re-rendering left stale references and the image stopped following the choice)
const showVariantMedia = (variant, speed = 300) => {
  if (!variant || !variant.featured_media) return
  updateSlide(variant.featured_media.id, speed)
}

// Thickness follows the chosen variant; it is shown as text instead of being its own dropdown
const updateThickness = (variant) => {
  if (!dom.thicknessValue || !state.thickness) return
  const value = state.thickness[variant.id] || ''
  dom.thicknessValue.textContent = value
  if (dom.thickness) dom.thickness.style.display = value ? '' : 'none'
}

// Inside a collection such as Polo, open on the first shape the collection asks for.
// Option values are translated, so the collection field may list several prefixes: "Show jumping, Springen"
const preselectShape = () => {
  const wanted = (dom.section.dataset.preselectShape || '').split(',').map((part) => part.trim().toLowerCase()).filter(Boolean)
  if (!wanted.length || state.urlHadVariant) return

  dom.optionSelects.forEach((select) => {
    if (state.shapePreselected) return
    const input = select.querySelector(`[${qsAll.options}]`)
    const matches = (text) => wanted.some((want) => text.toLowerCase().indexOf(want) === 0)
    if (!input || matches(input.value)) return

    const option = [...select.querySelectorAll('[data-value]')].find((value) => matches(value.dataset.value))
    if (!option) return
    option.click()
    state.shapePreselected = true
  })
}

const updatePrice = (variant) => {
  // Only the latest selection may update the price
  if (state.priceRequest) state.priceRequest.abort()
  const request = new AbortController()
  state.priceRequest = request

  fetch(`${location.pathname}?variant=${variant.id}`, { signal: request.signal })
    .then(response => response.text())
    .then((responseText) => {
      const priceHTML = new DOMParser().parseFromString(responseText, 'text/html').getElementById('pdpPrice').innerHTML
      dom.section.querySelector('#pdpPrice').innerHTML = priceHTML
    })
    .catch((error) => {
      if (error.name !== 'AbortError') console.error(error)
    })
}

const updateVariant = () => {
  const newVariantTitle = dom.options.map((option) => option.value).join(' / ')
  const variant = state.jsonData.variants.find((variant) => variant.title == newVariantTitle)

  // The chosen combination doesn't exist: don't leave the previous variant in the cart form
  if (!variant) {
    dom.addToCart.disabled = true
    dom.addToCart.innerHTML = window.variantStrings.unavailable
    return
  }

  dom.variantId.value = variant.id

  updateUrl()
  showVariantMedia(variant)
  updatePrice(variant)
  updateThickness(variant)

  // Update wishlists
  dom.section.querySelectorAll(`[${qsAll.wishlists}]`).forEach((wishlist) => {
    if(parseInt(wishlist.dataset.variantId) == variant.id)
      wishlist.style.display = ''
    else
      wishlist.style.display = 'none'
  })

  dom.addToCart.disabled = !variant.available
  dom.addToCart.innerHTML = variant.available ? window.variantStrings.addToCart : window.variantStrings.soldOut
}

const updateQty = (change) => {
  dom.quantity.value = Math.max(1, parseInt(dom.quantity.value) + change)
}

const updateMarkingVisibility = () => {
  const checkedValue = dom.section.querySelector(`[${qsAll.markingOptions}]:checked`).value
  dom.markingGroup.classList.toggle('hidden', checkedValue == 'Cut out letters')
  dom.markingGroup.querySelectorAll('input').forEach((input) => checkedValue == 'Cut out letters' ? input.removeAttribute('name') : input.setAttribute('name', input.dataset.property))
}

const updateSlide = (mediaId, speed = 300) => {
  const galleryItem = dom.galleryItems.find((galleryItem) => galleryItem.dataset.mediaId == mediaId)
  if (!galleryItem || !state.swiperGallery) return
  state.swiperGallery.slideToLoop(parseInt(galleryItem.dataset.index), speed)
}

const updateUrl = () => {
  history.pushState({}, "", `${location.pathname}?variant=${dom.variantId.value}`)
}

const initImages = () => {
  state.swiperGallery = new Swiper(dom.gallery, {
    speed: 300,
    slidesPerView: 1,
    loop: true,
    effect: 'fade',
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    },
    on: {
      slideChange: () => {
        if(state.swiperGallery) {
          setTimeout(() => {
            const mediaId = dom.gallery.querySelector('.swiper-slide-active').dataset.mediaId
            dom.thumbs.forEach((thumb) => {
              if(thumb.dataset.mediaId == mediaId)
                thumb.classList.add('pdp-thumb--active')
              else
                thumb.classList.remove('pdp-thumb--active')
            })
          }, 100)
        }
      }
    },
  })
  dom.thumbs.forEach(thumb => {
    thumb.addEventListener('click', (e) => {
      updateSlide(thumb.dataset.mediaId)
    })
  })
}

const initProduct = () => {
  // updateUrl() adds ?variant= right below, so remember whether the visitor arrived with one
  state.urlHadVariant = location.search.indexOf('variant=') !== -1
  updateUrl()
  state.jsonData = JSON.parse(dom.json.innerHTML)
  if(dom.letters) {
    state.maxLetters = parseInt(dom.letters.dataset.markingLetters)
    dom.letters.addEventListener('input', (e) => {
      if(e.target.value.length > state.maxLetters) e.target.value = e.target.value.substring(0, state.maxLetters)
    })
  }
  console.log(state.jsonData)
  
  if (dom.thicknessJson) {
    try {
      state.thickness = JSON.parse(dom.thicknessJson.textContent)
    } catch (error) {
      console.error('Could not read variant thickness data:', error)
    }
  }

  initImages()
  showVariantMedia(currentVariant(), 0)
  updateThickness(currentVariant())

  dom.options.forEach(option => option.addEventListener('input', () => updateVariant()))
  dom.qtyMinus.addEventListener('click', (e) => {
    e.preventDefault()
    updateQty(-1)
  })
  dom.qtyPlus.addEventListener('click', (e) => {
    e.preventDefault()
    updateQty(1)
  })
  dom.markingOptions.forEach((markingOption) => {
    markingOption.addEventListener('input', () => updateMarkingVisibility())
  })
  dom.addToCart.addEventListener('click', (e) => {
    e.stopPropagation()
    e.preventDefault()
    window.addLineItem(null, new FormData(dom.form), true, e.target)
    e.target.disabled = true
  })
  dom.optionSelects.forEach((select) => {
    select.addEventListener('click', (e) => {
      e.stopPropagation()
      select.querySelector(`[${qs.optionList}]`).classList.toggle('!grid')
    })
    select.querySelector(`[${qs.optionList}]`).addEventListener('click', (e) => {
      e.preventDefault()
    })
    const values = select.querySelectorAll('[data-value]')
    values.forEach((value) => {
      value.addEventListener('click', (e) => {
        e.preventDefault()
        e.stopPropagation()
        const variantValue = value.dataset.value
        values.forEach(v => v.classList.remove('bg-black', 'bg-opacity-20'))
        value.classList.add('bg-black', 'bg-opacity-20')
        select.querySelector(`[${qs.optionList}]`).classList.remove('!grid')
        select.querySelector(`[${qsAll.options}]`).value = variantValue
        select.querySelector('.pdp-option--select__value').innerHTML = variantValue
        const newInput = new Event('input')
        select.querySelector(`[${qsAll.options}]`).dispatchEvent(newInput)
      })
    })
  })
  window.addEventListener('click', (e) => {
    dom.optionSelects.forEach(select => select.querySelector(`[${qs.optionList}]`).classList.remove('!grid'))
  })

  // Last, so the option listeners above are already in place
  preselectShape()
}

export const setupProductSection = () => {
  cacheDom(dom, qs, qsAll)
  if(dom.section) initProduct()
}